'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import { CheckCircle, Calendar, Star, History } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const benefits = [
  { icon: Calendar, text: 'View and manage all your upcoming bookings' },
  { icon: History, text: 'Access your full booking history and receipts' },
  { icon: Star, text: 'Get early access to promotions and loyalty rewards' },
  { icon: CheckCircle, text: 'One-tap rescheduling and cancellation' },
];

export default function AuthScreen() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 booking-gradient flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute top-1/2 right-8 w-48 h-48 rounded-full bg-white/5" />
        </div>

        {/* Logo */}
        <Link href="/" className="flex items-center gap-3 relative z-10">
          <AppLogo size={40} />
          <span className="font-extrabold text-2xl tracking-tight text-white">
            Detail<span className="text-orange-300">Book</span>
          </span>
        </Link>

        {/* Center Content */}
        <div className="relative z-10">
          <h2 className="text-3xl xl:text-4xl font-800 text-white leading-tight mb-4">
            Your car deserves<br />the best care.
          </h2>
          <p className="text-white/70 text-base mb-8 leading-relaxed">
            Book professional detailing services in minutes. No phone calls, no hassle.
          </p>

          <div className="flex flex-col gap-3">
            {benefits?.map((b) => {
              const Icon = b?.icon;
              return (
                <div key={`benefit-${b?.text?.slice(0, 20)}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                    <Icon size={15} className="text-white" />
                  </div>
                  <p className="text-white/85 text-sm">{b?.text}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom testimonial */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 relative z-10">
          <div className="flex gap-0.5 mb-2">
            {Array.from({ length: 5 }, (_, i) => (
              <Star key={`star-${i}`} size={13} className="text-orange-300 fill-orange-300" />
            ))}
          </div>
          <p className="text-white/90 text-sm italic leading-relaxed">
            "My car looked brand new after the full detail. Booking was super easy and the reminder text was a nice touch."
          </p>
          <p className="text-white/60 text-xs mt-2 font-500">— Priya Venkataraman, Chicago IL</p>
        </div>
      </div>
      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Logo */}
        <div className="lg:hidden flex items-center gap-2 p-6 border-b border-border">
          <AppLogo size={32} />
          <span className="font-extrabold text-lg text-foreground">
            Detail<span className="text-primary">Book</span>
          </span>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
          <div className="w-full max-w-md">
            {/* Tab Switcher */}
            <div className="flex gap-1 bg-muted p-1 rounded-xl mb-8">
              <button
                onClick={() => setTab('login')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all ${
                  tab === 'login' ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setTab('signup')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all ${
                  tab === 'signup' ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Create Account
              </button>
            </div>

            {tab === 'login' ? (
              <LoginForm onSwitchToSignup={() => setTab('signup')} />
            ) : (
              <SignUpForm onSwitchToLogin={() => setTab('login')} />
            )}

            {/* Guest Booking CTA */}
            <div className="mt-6 text-center">
              <div className="flex items-center gap-3 mb-4">
                <hr className="flex-1 border-border" />
                <span className="text-xs text-muted-foreground font-500">or</span>
                <hr className="flex-1 border-border" />
              </div>
              <Link
                href="/"
                className="btn-secondary w-full justify-center text-sm"
              >
                Continue as Guest — Book without an account
              </Link>
              <p className="text-xs text-muted-foreground mt-2">
                No account needed. Confirmation sent to your email.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}